/**
 * AI Credits API — typed client for the credits surface (gateway prefix
 * /api/v1/credits). Mirrors services-python/ai-orchestrator-service/src/credits/
 * service.py (_snapshot, _tx_to_dict, _usage_to_dict, _result) and the route
 * signatures in src/routes/credits_api.py.
 *
 * Convention: 1 credit = 0.01 USD.
 */

import { api } from '@/lib/api/client';

const BASE = '/api/v1/credits';

export type TransactionType = 'topup' | 'reserve' | 'settle' | 'release';

/** Tenant wallet: balance owned, reserved for in-flight runs, available. */
export interface Wallet {
  id: string;
  tenant_id: string;
  low_balance_threshold: number;
  balance: number;
  reserved: number;
  available: number;
}

/** One immutable ledger line. */
export interface CreditTransaction {
  id: string;
  wallet_id: string;
  tenant_id: string;
  type: TransactionType;
  amount: number;
  balance_after: number;
  reserved_after: number;
  run_id: string | null;
  usage_record_id: string | null;
  idempotency_key: string | null;
  source: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string | null;
}

/** Per-call AI usage record (drives per-task / per-model consumption views). */
export interface UsageRecord {
  id: string;
  tenant_id: string;
  run_id: string | null;
  step_id: string | null;
  provider: string | null;
  model_id: string | null;
  task_type: string | null;
  tokens_in: number;
  tokens_out: number;
  cost_usd: number;
  credits: number;
  created_at: string | null;
}

export interface TopupBody {
  amount: number;
  note?: string | null;
  idempotency_key?: string | null;
}

/** A purchasable credit pack (Stripe checkout). Mirrors the /packs payload. */
export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  /** Price in the smallest currency unit (cents). */
  price_cents: number;
  /** Price as a decimal major-unit amount (e.g. 19.0). */
  price: number;
  currency: string;
  description: string;
  popular: boolean;
}

/** Catalogue of purchasable packs + whether Stripe checkout is wired. */
export interface PacksResponse {
  packs: CreditPack[];
  currency: string;
  stripe_enabled: boolean;
}

/** Result of creating a Stripe Checkout session. */
export interface CheckoutSession {
  session_id: string;
  url: string;
  pack_id: string;
  credits: number;
}

export interface CheckoutOptions {
  success_url?: string;
  cancel_url?: string;
}

/** Result of a wallet mutation (topup/reserve/settle/release). */
export interface TopupResult {
  transaction_id: string;
  type: TransactionType;
  amount: number;
  run_id: string | null;
  idempotency_key: string | null;
  idempotent_replay: boolean;
  balance: number;
  reserved: number;
  available: number;
}

function qs(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export function getWallet(): Promise<Wallet> {
  return api.get<Wallet>(`${BASE}/wallet`);
}

/** Wallet snapshot; auto-provisions the wallet + welcome grant server-side. */
export function getBalance(): Promise<Wallet> {
  return api.get<Wallet>(`${BASE}/balance`);
}

/** Purchasable packs catalogue + Stripe-enabled flag. */
export function getPacks(): Promise<PacksResponse> {
  return api.get<PacksResponse>(`${BASE}/packs`);
}

/**
 * Create a Stripe Checkout session for a pack. The returned `url` is the
 * Stripe-hosted page the caller redirects to. Throws `ApiError` 503 with
 * body `{error:"stripe_not_configured"}` when Stripe is not configured.
 */
export function createCheckout(
  packId: string,
  opts: CheckoutOptions = {},
): Promise<CheckoutSession> {
  return api.post<CheckoutSession>(`${BASE}/checkout`, {
    pack_id: packId,
    success_url: opts.success_url,
    cancel_url: opts.cancel_url,
  });
}

export function listTransactions(
  params: { limit?: number; offset?: number } = {},
): Promise<CreditTransaction[]> {
  return api.get<CreditTransaction[]>(`${BASE}/transactions${qs(params)}`);
}

export function listUsage(
  params: { run_id?: string; limit?: number } = {},
): Promise<UsageRecord[]> {
  return api.get<UsageRecord[]>(`${BASE}/usage${qs(params)}`);
}

export function topup(body: TopupBody): Promise<TopupResult> {
  return api.post<TopupResult>(`${BASE}/topup`, body);
}

// --- L-resume: re-dispatch a credit-blocked run after top-up ----------------

export interface RetryRunResult {
  status: string; // dispatched | already_dispatched | <run status>
  run_id: string;
  dispatched_run_id?: string | null;
  already_dispatched?: boolean;
}

/**
 * Re-run a run that was blocked at zero balance (status `blocked_credits`).
 * Idempotent on the run id. Throws `ApiError` 402 (with `blocked_run_id`) when
 * credits are STILL short, or 400 when the run cannot be resumed.
 */
export function retryBlockedRun(runId: string): Promise<RetryRunResult> {
  return api.post<RetryRunResult>(`/api/v1/runs/${encodeURIComponent(runId)}/retry`, {});
}

// --- L-autorefill: off-session Stripe top-up at a threshold -----------------

export interface AutoRefillConfig {
  enabled: boolean;
  threshold: number;
  pack_id: string | null;
  has_payment_method: boolean;
  customer_id?: string | null;
}

export function getAutoRefill(): Promise<AutoRefillConfig> {
  return api.get<AutoRefillConfig>(`${BASE}/auto-refill`);
}

export function updateAutoRefill(
  body: { enabled?: boolean; threshold?: number; pack_id?: string | null },
): Promise<AutoRefillConfig> {
  return api.put<AutoRefillConfig>(`${BASE}/auto-refill`, body);
}

/** Start a Stripe setup-mode Checkout to SAVE a card off-session → {url}. */
export function setupAutoRefillPaymentMethod(
  opts: { success_url?: string; cancel_url?: string } = {},
): Promise<{ url: string; session_id: string; customer_id: string }> {
  return api.post(`${BASE}/auto-refill/setup`, {
    success_url: opts.success_url,
    cancel_url: opts.cancel_url,
  });
}

// --- L-model: per-task model picker (Auto + admin allow-list) ---------------

export interface PickerModel {
  id: string;
  model_id: string;
  provider: string;
  label: string;
  credit_multiplier: number;
  credits_per_1k_input: number;
  credits_per_1k_output: number;
}

export interface PickerModelsResponse {
  default: { id: string; label: string; auto: boolean };
  models: PickerModel[];
  count: number;
}

/** Allow-listed models for the ModelPicker (Auto is always the default). */
export function listPickerModels(): Promise<PickerModelsResponse> {
  return api.get<PickerModelsResponse>(`/api/v1/models/picker`);
}
